'use client';

import { useEffect, useRef } from 'react';
import Image from 'next/image';
import { cn, prefersReducedMotion } from '@/lib/utils';

/**
 * Real product screenshots in device chrome. No fake div-built UI anywhere:
 * every visual on the products section is an actual screenshot shipped from
 * the product repos (converted to WebP under /public/images/products).
 */

export function BrowserShot({
  src,
  alt,
  url,
  accent,
  className,
  aspect = 'aspect-[16/10]',
  priority = false,
  sizes = '(max-width: 768px) 100vw, 70vw',
}: {
  src: string;
  alt: string;
  /** Shown in the fake address pill, e.g. crm.dilaweb.com */
  url?: string;
  accent?: string;
  className?: string;
  aspect?: string;
  priority?: boolean;
  sizes?: string;
}) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border border-white/10 bg-[#15151a] shadow-[0_40px_120px_-30px_rgba(0,0,0,0.8)]',
        className,
      )}
    >
      <div className="flex items-center gap-3 border-b border-white/10 bg-[#0d0d10] px-4 py-2.5">
        <span className="flex gap-1.5" aria-hidden="true">
          <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
        </span>
        {url ? (
          <span className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 font-mono text-[10px] tracking-[0.12em] text-white/50">
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: accent ?? 'var(--color-accent)' }}
              aria-hidden="true"
            />
            {url}
          </span>
        ) : null}
      </div>
      <div className={cn('relative w-full', aspect)}>
        <Image
          src={src}
          alt={alt}
          fill
          priority={priority}
          sizes={sizes}
          className="object-cover object-top"
        />
      </div>
    </div>
  );
}

export function PhoneShot({
  src,
  alt,
  className,
  priority = false,
  sizes = '(max-width: 768px) 60vw, 22vw',
}: {
  src: string;
  alt: string;
  className?: string;
  priority?: boolean;
  sizes?: string;
}) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-[2.2rem] border border-white/15 bg-[#0b0b0d] p-[5px] shadow-[0_50px_120px_-30px_rgba(0,0,0,0.85)]',
        className,
      )}
    >
      <div className="relative aspect-[9/19] w-full overflow-hidden rounded-[1.85rem] bg-[#15151a]">
        <Image
          src={src}
          alt={alt}
          fill
          priority={priority}
          sizes={sizes}
          className="object-cover object-top"
        />
      </div>
    </div>
  );
}

/**
 * Subtle pointer tilt for hero shots — the screenshot behaves like a physical
 * object. rAF-coalesced, no React state, mouse-only, reduced-motion safe.
 */
export function Tilt({
  children,
  className,
  max = 4,
}: {
  children: React.ReactNode;
  className?: string;
  /** Max degrees of rotation on each axis. */
  max?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const frame = useRef(0);

  useEffect(() => () => cancelAnimationFrame(frame.current), []);

  const onMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse' || prefersReducedMotion()) return;
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      el.style.transform = `perspective(1200px) rotateX(${(-py * max).toFixed(2)}deg) rotateY(${(px * max).toFixed(2)}deg)`;
    });
  };

  const onLeave = () => {
    cancelAnimationFrame(frame.current);
    const el = ref.current;
    if (el) el.style.transform = 'perspective(1200px) rotateX(0deg) rotateY(0deg)';
  };

  return (
    <div
      ref={ref}
      onPointerMove={onMove}
      onPointerLeave={onLeave}
      className={cn('transition-transform duration-300 ease-out will-change-transform', className)}
    >
      {children}
    </div>
  );
}
